import { useQuery } from "@tanstack/react-query";

import {
  IPatientTreatment,
  IPatientTreatmentError,
  ITreatmentSummary,
} from "../../../@types/admin/AdminTreatment";
import { treatmentsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Cross-patient treatment summary — the page's list query. */
export function useTreatmentsSummary() {
  const { data, isLoading, isError } = useQuery<ITreatmentSummary[]>({
    queryKey: ["admin-treatments-summary"],
    queryFn: async () =>
      ensureOk(await treatmentsService.getTreatmentsSummary()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { treatmentsSummary: data ?? [], isLoading, isError };
}

/**
 * One patient's device errors. Idle until a patient is selected, matching
 * Retool's `queryDisabled="{{ modalTreatmentErrors.hidden }}"` — the errors
 * were only fetched once the drill-down was open.
 */
export function usePatientTreatmentErrors(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientTreatmentError[]>({
    queryKey: ["admin-treatment-errors", patientId],
    queryFn: async () =>
      ensureOk(await treatmentsService.getPatientTreatmentErrors(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { treatmentErrors: data ?? [], isLoading, isError };
}

/** One patient's treatments — backs the shared `PatientTreatmentsPanel`. */
export function usePatientTreatments(patientId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IPatientTreatment[]>({
    queryKey: ["admin-patient-treatments", patientId],
    queryFn: async () =>
      ensureOk(await treatmentsService.getPatientTreatments(patientId!)),
    enabled: patientId !== undefined,
    refetchOnMount: "always",
  });

  return { treatments: data ?? [], isLoading, isError };
}
